import { createApi } from '@reduxjs/toolkit/query/react';

import { HTTP_METHOD } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getUrlParamsString,
} from '../generic';
import type {
  IListUserNotificationsResponse,
  IMarkNotificationsReadBody,
  IMarkNotificationsReadResponse,
  INotificationsListQueryArg,
  IUnreadNotificationsCountResponse,
} from './notificationsApi.types';

export {
  NotificationCategory,
  NotificationDisplayDomain,
  NotificationPreferenceGroup,
  NotificationSource,
} from './notificationsApi.types';

export type {
  IListUserNotificationsResponse,
  IMarkNotificationsReadBody,
  IMarkNotificationsReadResponse,
  INotificationsListQueryArg,
  IUnreadNotificationsCountResponse,
  IUserNotificationItem,
} from './notificationsApi.types';

export const NOTIFICATIONS_TAG = 'Notifications' as const;
export const UNREAD_COUNT_TAG = 'UnreadCount' as const;
export const UNREAD_COUNT_TAG_ID = 'COUNT' as const;

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  tagTypes: [NOTIFICATIONS_TAG, UNREAD_COUNT_TAG],
  baseQuery: getBaseQuery(),
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getNotifications: builder.query<
      IListUserNotificationsResponse,
      INotificationsListQueryArg | void
    >({
      query: (params) => {
        const p = (params ?? {}) as INotificationsListQueryArg;
        const qs = getUrlParamsString({
          limit: p.limit,
          cursor: p.cursor,
        });
        const url = qs
          ? `${endpoints.Notifications.LIST}?${qs}`
          : endpoints.Notifications.LIST;
        return { url, method: HTTP_METHOD.GET };
      },
      providesTags: [NOTIFICATIONS_TAG],
    }),
    getUnreadNotifications: builder.query<
      IListUserNotificationsResponse,
      INotificationsListQueryArg | void
    >({
      query: (params) => {
        const p = (params ?? {}) as INotificationsListQueryArg;
        const qs = getUrlParamsString({
          limit: p.limit,
          cursor: p.cursor,
        });
        const url = qs
          ? `${endpoints.Notifications.UNREAD}?${qs}`
          : endpoints.Notifications.UNREAD;
        return { url, method: HTTP_METHOD.GET };
      },
      providesTags: [NOTIFICATIONS_TAG],
    }),
    getUnreadCount: builder.query<IUnreadNotificationsCountResponse, void>({
      query: () => ({
        url: endpoints.Notifications.UNREAD_COUNT,
        method: HTTP_METHOD.GET,
      }),
      providesTags: [{ type: UNREAD_COUNT_TAG, id: UNREAD_COUNT_TAG_ID }],
    }),
    markNotificationsRead: builder.mutation<
      IMarkNotificationsReadResponse,
      IMarkNotificationsReadBody
    >({
      query(body) {
        return {
          url: endpoints.Notifications.READ,
          method: HTTP_METHOD.PATCH,
          body,
        };
      },
      invalidatesTags: [
        NOTIFICATIONS_TAG,
        { type: UNREAD_COUNT_TAG, id: UNREAD_COUNT_TAG_ID },
      ],
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useMarkNotificationsReadMutation,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useGetUnreadNotificationsQuery,
  useLazyGetUnreadNotificationsQuery,
  useGetUnreadCountQuery,
} = notificationsApi;
